import { Button } from '@automata/ui';
import { logger } from '@automata/utils';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Stack from '@mui/material/Stack';
import { FC, useState } from 'react';

import { AddDataFieldModal } from './AddDataFieldModal';
import { DataFieldList } from './DataFieldList';
import { EditDataFieldModal } from './EditDataFieldModal';
import type { AdditionalDataInput } from '../../../../types';
import { TextFieldWithMaxLength } from '../../../TextFieldWithMaxLength';
import { FormSectionLabel } from '../FormSectionLabel';

export type ContinuousLoadingFieldsProps = {
  nickname: string;
  onNicknameChange: (nickname: string) => void;
  onBlur: () => void;
  additionalDataInputs?: AdditionalDataInput[];
  onDataFieldsChange: (fields: AdditionalDataInput[]) => void;
};

export const ContinuousLoadingFields: FC<ContinuousLoadingFieldsProps> = ({
  nickname,
  onNicknameChange,
  onBlur,
  additionalDataInputs,
  onDataFieldsChange,
}) => {
  const actualFields = additionalDataInputs || [];
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);

  const existingFieldIds = actualFields.map((field) => field.id) || [];
  const dataInputIndex = actualFields.findIndex((f) => f.id === editingFieldId);
  const editingField =
    editingFieldId && dataInputIndex !== undefined && dataInputIndex !== -1
      ? actualFields[dataInputIndex]
      : null;

  const handleAddField = (field: AdditionalDataInput) => {
    const updated = [...actualFields, field];
    onDataFieldsChange(updated);
    setAddModalOpen(false);
  };

  const handleEditField = (fieldId: string) => {
    setEditingFieldId(fieldId);
  };

  const handleSaveField = (
    oldId: string,
    updatedField: AdditionalDataInput
  ) => {
    const updated = [...actualFields];
    const index = updated.findIndex((f) => f.id === oldId);

    if (index === -1) {
      // this should not happen -- cancel editing if input is missing
      logger.error(`Failed to save field. Field with id ${oldId} not found.`);
      setEditingFieldId(null);
      return;
    }
    updated[index] = updatedField;

    onDataFieldsChange(updated);
    setEditingFieldId(null);
  };

  const handleDeleteField = (fieldId: string) => {
    const updated = [...actualFields];
    const index = updated.findIndex((f) => f.id === fieldId);
    if (index === -1) {
      // this should not happen -- cancel deleting if input is missing
      logger.error(
        `Failed to delete field. Field with id ${fieldId} not found.`
      );
      setEditingFieldId(null);
      return;
    }
    updated.splice(index, 1);
    onDataFieldsChange(updated);
    setEditingFieldId(null);
  };

  const handleReorderFields = (fields: AdditionalDataInput[]) => {
    onDataFieldsChange(fields);
  };

  return (
    <Stack gap={1}>
      <Box mt={2}>
        <FormSectionLabel>Settings</FormSectionLabel>
      </Box>

      <FormControl fullWidth>
        <TextFieldWithMaxLength
          label="Nickname"
          maxLength={50}
          value={nickname}
          onChange={(e) => onNicknameChange(e.target.value)}
          onBlur={onBlur}
        />
      </FormControl>

      <Stack my={3} gap={2}>
        <FormSectionLabel>Data form</FormSectionLabel>

        <DataFieldList
          fields={actualFields}
          onEditField={handleEditField}
          onReorderFields={handleReorderFields}
        />

        <Button
          variant="contained"
          fullWidth
          onClick={() => setAddModalOpen(true)}
          startIcon={<AddIcon />}
          sx={{ mt: 1 }}
        >
          Add new field
        </Button>

        <AddDataFieldModal
          open={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          onAdd={handleAddField}
          existingFieldIds={existingFieldIds}
        />

        {editingField && (
          <EditDataFieldModal
            open={!!editingFieldId}
            onClose={() => setEditingFieldId(null)}
            field={editingField}
            onSave={handleSaveField}
            onDelete={handleDeleteField}
            existingFieldIds={existingFieldIds}
          />
        )}
      </Stack>
    </Stack>
  );
};
